import React, { useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { Close, Download } from '@mui/icons-material';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { useCreateDatabase } from '../../../api/bitedb/database/database.hooks';
import {
  useCreateBulkProductsWithoutDuplicateCheck,
  productTemplateColumns,
} from '../../../api/bitedb/products/products.hooks';
import {
  getProductPresignedUrl,
  createBulkProductsWithFile,
} from 'api/bitedb/products/products.service';
import { csvJSON } from '../../../utils/CsvToJson';
import * as XLSX from 'xlsx';

/* MUI icons */
import NewspaperIcon from '@mui/icons-material/Newspaper';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Box, styled } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EntriesFilterRow from 'components/Monitor/ResultsTab/TableFilters/EntriesFilterRow';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/* Other icons */
import assembly from 'resources/img/icon/assembly.png';
import component from 'resources/img/icon/component.png';
import customRisk from 'resources/img/icon/customRisk.png';
import financialExposue from 'resources/img/icon/financialExposure.png';
import infoIcon from 'resources/img/icon/info.png';
import regulatoryRisk from 'resources/img/icon/regulatoryRisk.png';
import rightLine from 'resources/img/icon/right-line.png';
import subAssembly from 'resources/img/icon/subAssembly.png';

interface ICreateDatabaseModalProps {
  open: boolean;
  handleClose: () => void;
  handleRefresh?: any;
  setTabState?: any;
  onDatabaseCreated?: (databaseId: string) => void;
  onDatabaseUploadFailed?: (databaseId: string) => void;
}

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--text-classify)',
  },
  overflow: 'hidden',
  color: 'var(--Text-Main, var(--text-primary))',
  textOverflow: 'ellipsis',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  borderRadius: '8px',
  border: '1px solid #2E3036',
});

const AIDisclaimerBox = styled(Box)({
  backgroundColor: 'var(--bg-products)',
  border: '1px solid #3E424B',
  borderRadius: '8px',
  padding: '16px',
  marginTop: '16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '5px',
});

const CreateDatabaseModal: React.FC<ICreateDatabaseModalProps> = ({
  handleClose,
  handleRefresh,
  onDatabaseCreated,
}) => {
  const [savingDatabase] = React.useState(false);
  const [databaseName, setDatabaseName] = React.useState('');
  const [isDBNameNotified, setIsDBNameNotified] = useState<boolean>(false);
  const [description, setDescription] = React.useState('');
  const [frequency, setFrequency] = React.useState('Once');
  const [weekDay, setWeekDay] = useState<string>('-');
  const [subtype, setSubtype] = useState<string>('imports');
  const [databaseStructure, setDatabaseStructure] =
    useState<string>('hierarchy');
  const [selectedOptsType, setSelectedOptsType] = useState();
  const ticketType = [
    {
      id: 1,
      name: 'Email Notification',
    },
    {
      id: 2,
      name: 'In-app Notification',
    },
    {
      id: 3,
      name: 'Daily Updates Notification',
    },
  ];
  const { cur_user } = React.useContext<any>(GlobalContext);
  const [step, setStep] = useState<number>(1);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<'idle' | 'creating' | 'uploading'>(
    'idle',
  );
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [cleanData, setCleanData] = useState<any[]>([]);
  const [exportData, setExportData] = useState<any[]>([]);
  const [showDuplicateStep, setShowDuplicateStep] = useState<boolean>(false);
  const [htsCodes, setHtsCodes] = useState<string[]>([]);
  const [showCSVRequired, setShowCSVRequired] = useState<boolean>(false);
  const [assemblies, setAssemblies] = useState<number>(0);
  const [subassemblies, setSubassemblies] = useState<number>(0);
  const [components, setComponents] = useState<number>(0);
  const [rawUploadedData, setRawUploadedData] = useState<any[]>([]);
  const [uploadedHeaders, setUploadedHeaders] = useState<string[]>([]);
  const [headerMappings, setHeaderMappings] = useState<Record<string, string>>(
    {},
  );
  const [notMappedColumnNames, setNotMappedColumnNames] = useState<
    Record<string, string>
  >({});

  /*const findCountryOption = (valueIsoOrName: string | undefined | null) => {
    if (!valueIsoOrName) {
      return null;
    }
    const v = String(valueIsoOrName).toUpperCase();
    return (
      (countries as COpt[]).find((c) => getIso(c)?.toUpperCase() === v) ||
      (countries as COpt[]).find((c) => getName(c).toUpperCase() === v) ||
      null
    );
  };*/

  const isCreatingDatabase = useRef(false);

  const handleSubtypeChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setSubtype(event.target.value as string);
  };

  const handleDatabaseStructureChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setDatabaseStructure(event.target.value as string);
  };

  const handleFrequencyChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setFrequency(event.target.value as string);
  };

  const handleWeekDayChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setWeekDay(event.target.value as string);
  };

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (bulkUploadProductsMutation.isPending) {
      return;
    }
    const accptFile = acceptedFiles[0];
    if (!accptFile) {
      return;
    }

    const fileNew = new File([accptFile], accptFile.name, {
      type: accptFile.type,
    });
    setFile(fileNew);

    try {
      await fileHandler(fileNew);
      enqueueSnackbar('File ready for submission!', { variant: 'success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error processing file. Please ensure it is a valid CSV, XLSX, or XML.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setFile(null);
    }
  };

  const fileHandler = async (fileToHandle: any) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (
          fileToHandle.type === 'text/csv' ||
          fileToHandle.name.endsWith('.csv') ||
          fileToHandle.name.endsWith('.xlsx') ||
          fileToHandle.name.endsWith('.xml') ||
          fileToHandle.type === 'text/xml' ||
          fileToHandle.type === 'application/xml'
        ) {
          resolve({
            name: fileToHandle.name,
            size: fileToHandle.size,
            type: fileToHandle.type,
          });
        } else {
          console.error(
            'Mock fileHandler error: Unsupported file type:',
            fileToHandle.type,
            fileToHandle.name,
          );
          reject(
            new Error(
              'Mock fileHandler: Unsupported file type. Please upload CSV, XLSX, or XML.',
            ),
          );
        }
      }, 500);
    });
  };

  const exportToCSV = (filename: string, data: any, headers: any) => {
    const csvRows = [];
    csvRows.push(headers.join(','));
    data.forEach((item: any) => {
      const values = headers.map((header: any) => {
        const val = item[header] ?? '';
        const escaped = `${val}`.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });
    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      enqueueSnackbar(
        'Browser does not support link.download attribute for mock CSV export.',
        { variant: 'error' },
      );
    }
  };

  const fileValidator = (currentFile: File) => {
    /*if (currentFile.size > 10 * 1024 * 1024) {
      enqueueSnackbar('Your file is too large, upload a file less than 10MB', {
        variant: 'error',
      });
      return { code: 'size-too-large', message: 'File is larger than 10MB' };
    }*/
    const acceptedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'text/xml',
      'application/xml',
    ];
    const acceptedExtensions = ['.csv', '.xlsx', '.xml'];

    const fileExtension = `.${currentFile.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;

    if (
      !acceptedMimeTypes.includes(currentFile.type) &&
      !acceptedExtensions.includes(fileExtension)
    ) {
      enqueueSnackbar(
        'Invalid file type. Please upload a CSV, Excel (XLSX), or XML file.',
        { variant: 'error' },
      );
      return {
        code: 'file-type-invalid',
        message: 'File type must be one of: .csv, .xlsx, .xml',
      };
    }
    return null;
  };

  const handleDeleteFile = () => {
    setFile(null);
    enqueueSnackbar('File removed.', { variant: 'info' });
  };

  const templateProductHeaders = useMemo(() => productTemplateColumns, []);

  const handleDownloadProductTemplate = () => {
    exportToCSV('product_template.csv', [], templateProductHeaders);
    enqueueSnackbar('Template download initiated.', { variant: 'info' });
  };

  const normalizeHeaderName = (header: string) =>
    header.trim().toLowerCase().replace(/\s+/g, '_');

  const initializeHeaderMappings = (headers: string[]) => {
    const mappedTemplateHeaders = new Set<string>();
    const initialMappings: Record<string, string> = {};
    const initialNotMappedNames: Record<string, string> = {};

    headers.forEach((header) => {
      const normalized = normalizeHeaderName(header);
      const exactTemplateMatch = templateProductHeaders.find(
        (templateHeader) => templateHeader === header,
      );
      const normalizedTemplateMatch = templateProductHeaders.find(
        (templateHeader) => templateHeader === normalized,
      );
      const matchedTemplateHeader =
        exactTemplateMatch || normalizedTemplateMatch;

      if (
        matchedTemplateHeader &&
        !mappedTemplateHeaders.has(matchedTemplateHeader)
      ) {
        initialMappings[header] = matchedTemplateHeader;
        mappedTemplateHeaders.add(matchedTemplateHeader);
      } else {
        initialMappings[header] = 'not_mapped';
      }

      initialNotMappedNames[header] = normalized;
    });

    setHeaderMappings(initialMappings);
    setNotMappedColumnNames(initialNotMappedNames);
  };

  const bulkUploadProductsMutation =
    useCreateBulkProductsWithoutDuplicateCheck();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/xml',
      'application/xml',
      '.csv',
      '.xlsx',
      '.xml',
    ],
    multiple: false,
  });

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) {
      return `${baseClass} dropzone-accept`;
    }
    if (isDragReject) {
      return `${baseClass} dropzone-reject`;
    }
    if (isDragActive) {
      return `${baseClass} dropzone-active`;
    }
    return file
      ? `${baseClass} dropzone-file-present`
      : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  const validateHierarchy = (hierarchyType: string) => {
    if (databaseStructure !== 'hierarchy') {
      return null;
    }
    if (!hierarchyType || hierarchyType.trim() === '') {
      return 'Assembly';
    }
    const validHierarchyType = hierarchyType.toLowerCase();
    if (
      validHierarchyType === 'assembly' ||
      validHierarchyType === 'assmbly' ||
      validHierarchyType === 'asmbly'
    ) {
      return 'Assembly';
    }
    if (
      validHierarchyType === 'subassembly' ||
      validHierarchyType === 'subassmbly' ||
      validHierarchyType === 'subasmbly' ||
      validHierarchyType === 'sub-assembly' ||
      validHierarchyType === 'sub assembly' ||
      validHierarchyType === 'sub-assmbly' ||
      validHierarchyType === 'sub-asmbly'
    ) {
      return 'Sub-Assembly';
    }
    if (
      validHierarchyType === 'component' ||
      validHierarchyType === 'components' ||
      validHierarchyType === 'bom' ||
      validHierarchyType === 'bom item' ||
      validHierarchyType === 'bomitem'
    ) {
      return 'Component';
    }
    return 'Assembly';
  };

  const readData = (e: any) => {
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        const text = e.target?.result as string;
        return csvJSON(text);
      } else if (file.name.endsWith('.xlsx')) {
        const buf = e.target?.result as ArrayBuffer;
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        return XLSX.utils.sheet_to_json(ws, { defval: '' });
      } else if (
        file.name.endsWith('.xml') ||
        file.type === 'text/xml' ||
        file.type === 'application/xml'
      ) {
        const text = e.target?.result as string;
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'application/xml');
        const parserError = doc.getElementsByTagName('parsererror')[0];

        if (parserError) {
          throw new Error(
            'Invalid XML file. Please upload a well-formed XML document.',
          );
        }

        const root = doc.documentElement;
        const itemElements = root.children;
        const rows: Record<string, string>[] = [];
        for (let i = 0; i < itemElements.length; i++) {
          const el = itemElements[i];
          const row: Record<string, string> = {};
          for (let j = 0; j < el.children.length; j++) {
            const child = el.children[j];
            row[child.tagName] = child.textContent ?? '';
          }
          if (Object.keys(row).length > 0) {
            rows.push(row);
          }
        }

        if (rows.length === 0) {
          throw new Error('XML file does not contain any product rows.');
        }

        return rows;
      }
    }
    return [];
  };

  const processUploadedData = (data: any[]) => {
    const groupedData = new Map<string, any[]>();
    const dups: any[] = [];
    const hierarchyTypeCounts = new Map<string, number>();
    const clean: any[] = [];
    const hts: string[] = [];
    const exportList: any[] = [];

    data.forEach((item: any) => {
      const hierarchyType: string | null = validateHierarchy(
        item.hierarchy_type,
      );

      item.hierarchy_type = hierarchyType;

      if (hierarchyType) {
        const count = hierarchyTypeCounts.get(hierarchyType);
        hierarchyTypeCounts.set(hierarchyType, count ? count + 1 : 1);
      }
      if (item.hts_code && !hts.includes(item.hts_code)) {
        hts.push(item.hts_code);
      }

      if (
        (!item.product_value || item.product_value === '') &&
        item.unit_value &&
        item.unit_value !== ''
      ) {
        item.product_value = item.unit_value;
      }

      const name = item.name || item.Name || '';
      const country =
        item.country_of_origin ||
        item.Country_Of_Origin ||
        item.country ||
        item.Country ||
        '';

      const htsCode = item.hts_code || '';
      const manufacturer = item.manufacturer || '';
      const vendor = item.vendor || '';
      if (!name.trim()) {
        return;
      }

      const key = `${name.trim().toLowerCase()}|${country
        .trim()
        .toLowerCase()}|${htsCode.trim().toLowerCase()}|${manufacturer
        .trim()
        .toLowerCase()}|${vendor.trim().toLowerCase()}`;

      if (!groupedData.has(key)) {
        groupedData.set(key, []);
      }
      groupedData.get(key)?.push(item);
    });

    groupedData.forEach((items) => {
      if (items.length > 1) {
        exportList.push(...items);
        clean.push(items[0]);
        dups.push(...items.slice(1));
      } else {
        clean.push(items[0]);
      }
    });

    if (dups.length > 0) {
      setDuplicates(dups);
      setExportData(exportList);
      setShowDuplicateStep(true);
    } else {
      setShowDuplicateStep(false);
    }

    setAssemblies(hierarchyTypeCounts.get('Assembly') || 0);
    setSubassemblies(hierarchyTypeCounts.get('Sub-Assembly') || 0);
    setComponents(hierarchyTypeCounts.get('Component') || 0);
    setCleanData(clean);
    setHtsCodes(hts);
    setStep(7);
  };

  const handleFlowSelect = async (flow: string) => {
    setDatabaseStructure(flow);
    handleStep('increase');
  };

  const handleStep = async (action: string) => {
    if (action === 'increase') {
      if (step === 2 && (!databaseName || databaseName.trim() === '')) {
        enqueueSnackbar('Please enter a database name', { variant: 'error' });
        setIsDBNameNotified(true);
        return;
      }

      setIsDBNameNotified(false);

      if (step === 4) {
        if (file) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            const data = readData(e);
            const headers = Object.keys(data?.[0] || {});
            setRawUploadedData(data);
            setUploadedHeaders(headers);
            initializeHeaderMappings(headers);
            setStep(5);
          };
          setShowCSVRequired(false);
          const fileName = file.name.toLowerCase();
          const isTextUpload =
            fileName.endsWith('.csv') ||
            fileName.endsWith('.xml') ||
            file.type === 'text/csv' ||
            file.type === 'text/xml' ||
            file.type === 'application/xml';

          if (isTextUpload) {
            reader.readAsText(file);
          } else {
            reader.readAsArrayBuffer(file);
          }
          return;
        }
        setShowCSVRequired(true);
        setStep(4);
        return;
      }

      if (step === 5) {
        if (!rawUploadedData.length) {
          enqueueSnackbar('Please upload a file before matching headers.', {
            variant: 'error',
          });
          setStep(4);
          return;
        }

        const mappedData = rawUploadedData.map((item: any) => {
          const mappedItem: Record<string, any> = {};
          const notMappedColumns: Record<string, any> = {};

          uploadedHeaders.forEach((uploadedHeader) => {
            const mappedHeader = headerMappings[uploadedHeader];
            if (!mappedHeader) {
              return;
            }

            if (mappedHeader === 'not_mapped') {
              const customHeader =
                normalizeHeaderName(
                  notMappedColumnNames[uploadedHeader] || '',
                ) || normalizeHeaderName(uploadedHeader);
              notMappedColumns[customHeader] = item[uploadedHeader] ?? '';
              return;
            }

            mappedItem[mappedHeader] = item[uploadedHeader] ?? '';
          });

          mappedItem.not_mapped_columns = notMappedColumns;

          return mappedItem;
        });

        processUploadedData(mappedData);
      }

      setStep(Math.min(step + 1, 8));
    }
    if (action === 'decrease') {
      setStep(Math.max(step - 1, 1));
    }
  };

  const handleTypeName = (e: any) => {
    setDatabaseName(e.target.value);
  };

  const handleTypeDescription = (e: any) => {
    setDescription(e.target.value);
  };

  const createDatabaseMutation = useCreateDatabase();

  const handleCreateDatabase = async () => {
    if (isCreatingDatabase.current || status !== 'idle') {
      return;
    }

    if (!databaseName) {
      enqueueSnackbar('Please enter a name for the database', {
        variant: 'error',
      });
      setIsDBNameNotified(true);
      return;
    }

    setIsDBNameNotified(false);

    try {
      isCreatingDatabase.current = true;
      setStatus('creating');

      const database = {
        name: databaseName,
        description: description,
        type: 'products',
        subtype: subtype ?? 'imports',
        database_structure: databaseStructure ?? 'hierarchy',
        group_id: cur_user?.selected_team?.id,
        created_by: cur_user?.basics?.id,
        screening_frequency: frequency,
        screening_day_of_week: weekDay,
        last_updated: Date.now().toString(),
        notifications_email: `${selectedOptsType}`,
        notifications_enabled: true,
      };

      const response = await createDatabaseMutation.mutateAsync(database);

      if (!response?.success || !response?.result?.data) {
        throw new Error(
          `Failed to create database: ${response?.result || 'Unknown error'}`,
        );
      }

      const createdDb = response.result.data;

      if (file) {
        const fileExt = file.name.endsWith('.csv')
          ? 'csv'
          : file.name.endsWith('.xml')
          ? 'xml'
          : 'xlsx';
        const s3ContentType =
          fileExt === 'csv'
            ? 'text/csv'
            : fileExt === 'xml'
            ? 'text/xml'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        onDatabaseCreated?.(createdDb.id.toString());
        setStatus('uploading');
        const response = await getProductPresignedUrl(
          fileExt,
          databaseName,
          cur_user?.selected_team?.id,
          cur_user?.selected_team?.name,
        );

        if (!response?.uploadUrl) {
          enqueueSnackbar(
            `Failed to get presigned URL for upload: ${
              response || 'Unknown error'
            }`,
            { variant: 'error' },
          );
        } else {
          const s3Response = await fetch(response?.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': s3ContentType,
            },
          });
          if (!s3Response.ok) {
            throw new Error(
              `Failed to upload file to S3: ${
                s3Response.statusText || 'Unknown error'
              }`,
            );
          } else {
            await createBulkProductsWithFile(
              notMappedColumnNames,
              s3Response.url,
              fileExt,
              createdDb.id.toString(),
              cur_user?.basics?.id,
              cur_user?.selected_team?.id,
            );
          }
        }
      }

      handleClose();
      handleRefresh();
    } catch (error: any) {
      console.error('Error in database creation flow:', error);
      enqueueSnackbar(
        error?.message ||
          'Error during database creation or upload. Please try again.',
        { variant: 'error' },
      );
    } finally {
      isCreatingDatabase.current = false;
      setStatus('idle');
    }
  };

  return (
    <>
      <div className='login-wizard-container-set-width'>
        <button
          onClick={handleClose}
          className='modal-close-button'
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#333';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#666';
          }}
        >
          <Close fontSize='medium' />
        </button>

        <br />
        <br />

        <div className='workflow-header workflow-header-padding'>
          <div className='workflow-item active'>
            <img src={infoIcon} alt='upload' /> Intro
          </div>
          <svg
            width='55'
            height='1'
            viewBox='0 0 55 1'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect
              width='55'
              height='1'
              fill={step >= 2 ? 'var(--action-color)' : 'var(--noaction-color)'}
            />
          </svg>
          <div className={step >= 3 ? 'workflow-item active' : 'workflow-item'}>
            <NewspaperIcon />
            CSV Template
          </div>
          <svg
            width='55'
            height='1'
            viewBox='0 0 55 1'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect
              width='55'
              height='1'
              fill={step >= 3 ? 'var(--action-color)' : 'var(--noaction-color)'}
            />
          </svg>
          <div className={step >= 4 ? 'workflow-item active' : 'workflow-item'}>
            <FileUploadIcon />
            Upload CSV
          </div>
          <svg
            width='55'
            height='1'
            viewBox='0 0 55 1'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect
              width='55'
              height='1'
              fill={step >= 4 ? 'var(--action-color)' : 'var(--noaction-color)'}
            />
          </svg>
          <div className={step >= 5 ? 'workflow-item active' : 'workflow-item'}>
            <VerifiedUserOutlinedIcon />
            Validation
          </div>
          <svg
            width='55'
            height='1'
            viewBox='0 0 55 1'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect
              width='55'
              height='1'
              fill={step >= 5 ? 'var(--action-color)' : 'var(--noaction-color)'}
            />
          </svg>
          <div className={step >= 6 ? 'workflow-item active' : 'workflow-item'}>
            <SettingsIcon />
            Settings
          </div>
          <svg
            width='55'
            height='1'
            viewBox='0 0 55 1'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect
              width='55'
              height='1'
              fill={step >= 7 ? 'var(--action-color)' : 'var(--noaction-color)'}
            />
          </svg>
          <div className={step >= 7 ? 'workflow-item active' : 'workflow-item'}>
            <CheckCircleOutlineOutlinedIcon />
            Confirmation
          </div>
        </div>

        {step === 1 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ height: 'fit-content' }}
          >
            <div className='tariff-buttons-cont'>
              <div className='disclaimer-text'>
                Disclaimer: This module provides automated compliance screening
                based on user-provided product data, including tariff checks,
                customs flag screening, denied party screening, and CROSS ruling
                references. Results are generated using available data sources
                and predefined logic and may not capture all applicable
                regulatory requirements or risks.
                <br />
                The accuracy of outputs depends on the completeness and
                correctness of the data provided by the user. These results are
                not a substitute for comprehensive compliance review or
                professional advice. Users are solely responsible for validating
                all findings and ensuring compliance with applicable customs,
                trade, and export regulations.
              </div>
            </div>
            <div
              className='audit-add-modal-head-cont-supporting'
              style={{ marginBottom: '0rem', paddingBottom: '0rem' }}
            >
              <div className='header-text'>Create Database</div>
              <p
                className='dialog-text'
                style={{ marginBottom: '0rem', textAlign: 'left' }}
              >
                Create a new database to organize your products, manage your
                inventory, and run screening against potential risks.
              </p>

              <div className='header-text' style={{ paddingTop: '10px' }}>
                How to store the products?
              </div>

              <div
                className='results-card-frame'
                style={{ paddingTop: '5px', paddingBottom: '5px' }}
              >
                <div className='results-card-frame-comp-top'>
                  <div className='results-card-frame-head'>
                    Hierarchy Flow{' '}
                    <span
                      className='link'
                      onClick={() => handleFlowSelect('hierarchy')}
                    >
                      Start
                    </span>
                  </div>
                  <div className='results-card-frame-bodys'>
                    <img src={assembly} alt='assembly' /> Assembly
                    <br />
                    &nbsp; &nbsp;
                    <img src={rightLine} alt='rightLine' />
                    <img src={subAssembly} alt='assembly' /> Sub-assembly
                    <br />
                    &nbsp; &nbsp;&nbsp; &nbsp;&nbsp;
                    <img src={rightLine} alt='rightLine' />
                    <img src={component} alt='assembly' /> BOM Item
                  </div>
                </div>

                <div className='results-card-frame-comp-top'>
                  <div className='results-card-frame-head'>
                    Flat Flow{' '}
                    <span
                      className='link'
                      onClick={() => handleFlowSelect('flat')}
                    >
                      Start
                    </span>
                  </div>
                  <div className='results-card-frame-bodys'>
                    Products will be organized in a flat structure without
                    hierarchy levels. Each product will be treated as an
                    individual item without parent-child relationships.
                  </div>
                </div>

                <div className='results-card-frame-comp-top'>
                  <div className='results-card-frame-head'>
                    HTS Code Grouping Flow
                    <span
                      className='link'
                      onClick={() => handleFlowSelect('hts-code-grouping')}
                    >
                      Start
                    </span>
                  </div>
                  <div className='results-card-frame-bodys'>
                    Products will be grouped based on their HTS codes, allowing
                    you to analyze and manage products that share similar
                    classifications together.
                  </div>
                </div>
              </div>

              <div className='header-text'>What will be analyzed?</div>

              <div
                className='results-card-frame'
                style={{ paddingTop: '5px', paddingBottom: '5px' }}
              >
                <div className='results-card-frame-comp-top'>
                  <div className='results-card-frame-head'>
                    <img src={regulatoryRisk} /> Regulatory Risk
                  </div>
                  <div className='results-card-frame-bodys'>
                    Eliminating danger of loss from failing to follow laws and
                    regulations, and a trade compliance tool reduces this risk
                    by automating checks, keeping rules current, and preventing
                    illegal or non-compliant trade activities.
                  </div>
                </div>
                <div className='results-card-frame-comp-top'>
                  <div className='results-card-frame-head'>
                    <img src={customRisk} /> Customs Risk
                  </div>
                  <div className='results-card-frame-bodys'>
                    Avoiding the risk that shipments may be delayed, penalized,
                    rejected, or seized by customs authorities due to incorrect,
                    incomplete, or non-compliant import/export information.
                  </div>
                </div>
                <div className='results-card-frame-comp-top'>
                  <div className='results-card-frame-head'>
                    <img src={financialExposue} /> Financial Exposure
                  </div>
                  <div className='results-card-frame-bodys'>
                    Minimizing risk that a company may incur direct or indirect
                    financial losses due to errors, non-compliance, or
                    disruptions in its business activities.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ height: '550px', overflowY: 'scroll' }}
          >
            <div className='audit-add-modal-head-cont-supporting'>
              <div className='header-text'>Database Info</div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <div className='database-form-group'>
                  <label
                    htmlFor='database-name'
                    className={
                      isDBNameNotified
                        ? 'audit-add-modal-cont-body-frame-form-row-input-label-text alert-color'
                        : 'audit-add-modal-cont-body-frame-form-row-input-label-text'
                    }
                  >
                    <h5>Database Name</h5>
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      (Choose a descriptive name for your database.)
                    </span>
                  </label>
                  <input
                    type='text'
                    id='database-name'
                    placeholder='Enter database name'
                    className='audit-add-modal-cont-body-frame-form-input'
                    value={databaseName}
                    onChange={handleTypeName}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className='database-form-group'>
                  <label
                    htmlFor='subtype'
                    className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                  >
                    <h5>Import/Export</h5>
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      (Select whether the database will be used for imports or
                      exports. This will help tailor the screening rules applied
                      to your products.)
                    </span>
                  </label>
                  <FormControl sx={{ width: '100%', height: '48px' }}>
                    <CustomSelect
                      value={subtype}
                      onChange={handleSubtypeChange}
                      fullWidth
                      IconComponent={KeyboardArrowDownIcon}
                      inputProps={{
                        MenuProps: {
                          MenuListProps: {
                            sx: {
                              backgroundColor: 'var(--bg-colors)',
                              color: 'var(--Text-Main, var(--text-primary))',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value={'imports'}>Import</MenuItem>
                      <MenuItem value={'exports'} disabled>
                        Export <strong>(Coming Soon)</strong>
                      </MenuItem>
                    </CustomSelect>
                  </FormControl>
                </div>
                <div className='database-form-group'>
                  <label
                    htmlFor='subtype'
                    className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                  >
                    <h5>Database Structure</h5>
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      (
                      {databaseStructure === 'hierarchy'
                        ? 'Assembly > Sub-assembly > Component'
                        : databaseStructure === 'flat'
                        ? 'Flat list of products'
                        : 'Products grouped by HTS code'}
                      )
                    </span>
                  </label>
                  <FormControl sx={{ width: '100%', height: '48px' }}>
                    <CustomSelect
                      value={databaseStructure}
                      onChange={handleDatabaseStructureChange}
                      fullWidth
                      IconComponent={KeyboardArrowDownIcon}
                      inputProps={{
                        MenuProps: {
                          MenuListProps: {
                            sx: {
                              backgroundColor: 'var(--bg-colors)',
                              color: 'var(--Text-Main, var(--text-primary))',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value={'hierarchy'}>
                        Assembly Hierarchy
                      </MenuItem>
                      <MenuItem value={'flat'}>Flat Products</MenuItem>
                      <MenuItem value={'hts-code-grouping'}>
                        HTS Code Product Grouping
                      </MenuItem>
                    </CustomSelect>
                  </FormControl>
                </div>
                <div className='database-form-group'>
                  <label
                    htmlFor='database-description'
                    className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                  >
                    <h5>Description</h5>
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      (Provide additional details about the database, such as
                      its intended use, data sources, or any specific screening
                      criteria you want to apply. This can help differentiate it
                      from other databases and provide context for future
                      reference.)
                    </span>
                  </label>
                  <textarea
                    id='database-description'
                    rows={3}
                    placeholder='Enter a description for your database'
                    className='audit-add-modal-cont-body-frame-form-input'
                    style={{ width: '100%', height: '100px' }}
                    value={description}
                    onChange={handleTypeDescription}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        )}
        {step === 3 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ height: '550px' }}
          >
            <div className='wizard-team-graphic-container'>
              <div className='audit-add-modal-cont-body-frame-form'>
                <div className='header-text'>CSV Template</div>
                <p className='dialog-text'>
                  Download a .csv template file with example headers. If you
                  have already downloaded the csv template, please proceed to
                  CSV Upload.
                </p>

                <button
                  onClick={handleDownloadProductTemplate}
                  className='button button-primary center-self'
                >
                  <Download style={{ marginRight: '8px' }} />
                  Download CSV Template
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ height: '550px' }}
          >
            <div className='wizard-team-graphic-container'>
              <div className='audit-add-modal-cont-body-frame-form'>
                <div className='header-text'>Upload File</div>
                <p className='dialog-text' style={{ marginBottom: '0rem' }}>
                  Please upload a CSV, Excel (XLSX), or XML file with the
                  correct headers from the CSV template.
                </p>
                <div {...getRootProps()} className={dropzoneClassName}>
                  <input {...getInputProps()} />
                  {file ? (
                    <div className='dropzone-content-item'>
                      <svg
                        className='dropzone-icon-success'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                        />
                      </svg>
                      <p className='dropzone-filename'>{file.name}</p>
                      <p className='dropzone-filesize'>
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFile();
                        }}
                        className='dropzone-remove-button'
                      >
                        Remove File
                      </button>
                    </div>
                  ) : bulkUploadProductsMutation.isPending ? (
                    <div className='dropzone-content-item'>
                      Processing file...
                    </div>
                  ) : (
                    <div className='dropzone-content-item'>
                      <p className='dropzone-text'>
                        <span className='dropzone-text-highlight'>
                          Click to upload
                        </span>{' '}
                        or drag and drop
                      </p>
                      <p className='dropzone-subtext'>
                        CSV, XLSX, or XML (max 10MB)
                      </p>
                      {isDragReject && (
                        <p className='dropzone-error-text'>
                          Invalid file type or size.
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {showCSVRequired && (
                  <p
                    className='dialog-text alert-color'
                    style={{ marginBottom: '0rem' }}
                  >
                    Please upload a file before proceeding.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ height: '550px', overflowY: 'auto' }}
          >
            <div className='audit-add-modal-cont-body-frame-form-stretch'>
              <div className='header-text'>Match Product Headers</div>
              <p className='dialog-text'>
                Exact header matches are pre-selected. Map each imported header
                to a template header, or mark it as Not mapped.
              </p>
              {uploadedHeaders.map((uploadedHeader) => (
                <div
                  key={uploadedHeader}
                  style={{
                    display: 'flex',
                    gap: '5px',
                    marginBottom: '8px',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ width: '40%', fontWeight: 600 }}>
                    {uploadedHeader}
                  </div>
                  <select
                    value={headerMappings[uploadedHeader] || ''}
                    onChange={(e) =>
                      setHeaderMappings((prev) => ({
                        ...prev,
                        [uploadedHeader]: e.target.value,
                      }))
                    }
                    className='audit-add-modal-cont-body-frame-form-input'
                    style={{ width: '60%', height: '40px' }}
                  >
                    <option value='not_mapped'>Not mapped</option>
                    {templateProductHeaders.map((templateHeader) => (
                      <option
                        key={`${uploadedHeader}-${templateHeader}`}
                        value={templateHeader}
                      >
                        {templateHeader}
                      </option>
                    ))}
                  </select>
                  {headerMappings[uploadedHeader] === 'not_mapped' && (
                    <input
                      type='text'
                      value={notMappedColumnNames[uploadedHeader] || ''}
                      onChange={(e) =>
                        setNotMappedColumnNames((prev) => ({
                          ...prev,
                          [uploadedHeader]: normalizeHeaderName(e.target.value),
                        }))
                      }
                      className='audit-add-modal-cont-body-frame-form-input'
                      style={{ width: '45%' }}
                      placeholder='imported_header_key'
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {step === 6 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ height: 'fit-content' }}
          >
            <div className='header-text'>Database Settings</div>
            {/* AI Disclaimer */}
            <AIDisclaimerBox>
              <div>
                <div className={'hit-title'}>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 18, color: '#ffc107', marginTop: '2px' }}
                  />
                  AI Screening Notice
                </div>
                <div
                  className={
                    'audit-add-modal-cont-body-frame-form-row-input-label-text'
                  }
                >
                  AI-powered screening checks are automatically disabled for
                  results containing more than 250 hits.
                </div>
              </div>
            </AIDisclaimerBox>

            <div className='hit-title'>
              <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} /> Screening
              Frequency
            </div>
            <div className='screening-settings-row'>
              <div
                className='audit-add-modal-cont-body-frame-form-stretch'
                style={{ width: '100%' }}
              >
                <div className='audit-add-modal-cont-body-frame-form-row'>
                  <div className='audit-add-modal-cont-body-frame-form-row-input'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                        Frequency
                      </div>
                      <FormControl sx={{ width: '100%', height: '48px' }}>
                        <CustomSelect
                          value={frequency}
                          onChange={handleFrequencyChange}
                          fullWidth
                          IconComponent={KeyboardArrowDownIcon}
                          inputProps={{
                            MenuProps: {
                              MenuListProps: {
                                sx: {
                                  backgroundColor: 'var(--bg-colors)',
                                  color:
                                    'var(--Text-Main, var(--text-primary))',
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value={'Once'}>Once</MenuItem>
                          <MenuItem value={'Daily'}>Daily</MenuItem>
                          <MenuItem value={'Weekly'}>Weekly</MenuItem>
                          <MenuItem value={'Monthly'}>Monthly</MenuItem>
                          <MenuItem value={'Yearly'}>Yearly</MenuItem>
                        </CustomSelect>
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>
              {frequency === 'Weekly' && (
                <div
                  className='audit-add-modal-cont-body-frame-form'
                  style={{ width: '100%' }}
                >
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                        <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                          Day of Week
                        </div>
                        <FormControl sx={{ width: '100%', height: '48px' }}>
                          <CustomSelect
                            value={weekDay}
                            onChange={handleWeekDayChange}
                            fullWidth
                            IconComponent={KeyboardArrowDownIcon}
                            inputProps={{
                              MenuProps: {
                                MenuListProps: {
                                  sx: {
                                    backgroundColor: 'var(--bg-colors)',
                                    color:
                                      'var(--Text-Main, var(--text-primary))',
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value={'Monday'}>Monday</MenuItem>
                            <MenuItem value={'Tuesday'}>Tuesday</MenuItem>
                            <MenuItem value={'Wednesday'}>Wednesday</MenuItem>
                            <MenuItem value={'Thursday'}>Thursday</MenuItem>
                            <MenuItem value={'Friday'}>Friday</MenuItem>
                            <MenuItem value={'Saturday'}>Saturday</MenuItem>
                            <MenuItem value={'Sunday'}>Sunday</MenuItem>
                          </CustomSelect>
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className='screening-settings-col'>
              <div className='hit-main'>
                <div className='hit-title'>
                  <MarkunreadOutlinedIcon sx={{ fontSize: 14 }} /> Email
                  Notifications
                </div>
              </div>
              <div className='screening-settings-row'>
                <div className='hit-main'>
                  <div className='hit-title'>
                    <strong>
                      Send Email Notifications / In-App Notification
                    </strong>
                  </div>
                  <div className='hit-meta'>
                    Get notified when screening results are available
                  </div>
                </div>
              </div>
              {ticketType.map((row: any, index: number) => (
                <div className='screening-settings-row' key={row.name}>
                  <EntriesFilterRow
                    key={index}
                    name={row.name}
                    selectedItems={selectedOptsType}
                    setSelectedItems={setSelectedOptsType}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 7 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ height: '550px', alignSelf: 'center' }}
          >
            <div className='audit-add-modal-head-cont-supporting'>
              <div className='header-text'>
                Ready to create Product Database?
              </div>
              {showDuplicateStep && (
                <div
                  className='team-create-container wizard-step-content'
                  style={{
                    minHeight: '420px',
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    className='audit-add-modal-head-cont-supporting'
                    style={{ paddingTop: '10px', textAlign: 'center' }}
                  >
                    <p
                      style={{
                        marginBottom: '1rem',
                        color: '#d32f2f',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                      }}
                    >
                      {duplicates.length} Duplicate Entities Found
                    </p>
                    <button
                      onClick={() => {
                        const headers = Object.keys(exportData[0] || {});
                        exportToCSV('duplicates.csv', exportData, headers);
                      }}
                      className='button button-secondary button-full-width'
                      style={{
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Download style={{ marginRight: '8px' }} />
                      Download Duplicates List
                    </button>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        marginTop: '10px',
                        width: '100%',
                      }}
                    >
                      <button
                        onClick={() => {
                          setShowDuplicateStep(false);
                        }}
                        className='button button-secondary'
                        style={{ width: '100%', padding: '12px' }}
                      >
                        Keep Duplicates
                      </button>
                      <button
                        onClick={() => {
                          if (cleanData.length > 0) {
                            const headers = Object.keys(cleanData[0]);
                            const csvRows = [];
                            csvRows.push(headers.join(','));
                            cleanData.forEach((item: any) => {
                              const values = headers.map((header: any) => {
                                const escaped = `${item[header]}`.replace(
                                  /"/g,
                                  '""',
                                );
                                return `"${escaped}"`;
                              });
                              csvRows.push(values.join(','));
                            });
                            const csvString = csvRows.join('\r\n');
                            const blob = new Blob([csvString], {
                              type: 'text/csv',
                            });
                            const newFile = new File(
                              [blob],
                              file?.name || 'cleaned_data.csv',
                              { type: 'text/csv' },
                            );
                            setFile(newFile);
                            enqueueSnackbar(
                              'Duplicates removed. Proceeding with unique entities.',
                              { variant: 'success' },
                            );
                            setShowDuplicateStep(false);
                          }
                        }}
                        className='button button-primary'
                        style={{
                          width: '100%',
                          backgroundColor: '#d32f2f',
                          borderColor: '#d32f2f',
                          padding: '12px',
                        }}
                      >
                        Remove Duplicates
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {!showDuplicateStep && (
                <>
                  <p className='dialog-text' style={{ marginBottom: '0rem' }}>
                    {databaseName} database will be created with the following
                    summary items.
                  </p>
                  <div className='results-card-frame'>
                    <div className='results-card-frame-comp'>
                      <div className='header-text'>Summary Items</div>
                      {databaseStructure === 'hierarchy' && (
                        <div className='results-card-frame-body center-text'>
                          <span
                            style={{
                              color: 'var(--action-color)',
                              fontWeight: 'bold',
                            }}
                          >
                            {assemblies}
                          </span>{' '}
                          Assemblies
                          <img src={rightLine} alt='rightLine' />
                          <span
                            style={{
                              color: 'var(--action-color)',
                              fontWeight: 'bold',
                            }}
                          >
                            {subassemblies}
                          </span>{' '}
                          Sub-assemblies
                          <img src={rightLine} alt='rightLine' />
                          <span
                            style={{
                              color: 'var(--action-color)',
                              fontWeight: 'bold',
                            }}
                          >
                            {components}
                          </span>{' '}
                          Components
                        </div>
                      )}
                      {databaseStructure === 'flat' && (
                        <div className='results-card-frame-body center-text'>
                          <img src={rightLine} alt='rightLine' />
                          <span
                            style={{
                              color: 'var(--action-color)',
                              fontWeight: 'bold',
                            }}
                          >
                            {cleanData.length}
                          </span>{' '}
                          Products
                        </div>
                      )}
                      {databaseStructure === 'hts-code-grouping' && (
                        <div className='results-card-frame-body center-text'>
                          <img src={rightLine} alt='rightLine' />
                          <span
                            style={{
                              color: 'var(--action-color)',
                              fontWeight: 'bold',
                            }}
                          >
                            {htsCodes.length}
                          </span>{' '}
                          HTS Codes
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className='wizard-btn-group'>
          <div className='wizard-btn-container'>
            <div className='wizard-btn-dropdown'>
              {savingDatabase ? (
                <div className='wizard-back-btn-disabled'>
                  <div className='wizard-btn-text-padding'>
                    <div className='wizard-back-btn-text-disabled'>
                      {step === 1 ? 'Close' : 'Back'}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={
                    step === 1
                      ? 'wizard-back-btn'
                      : `wizard-back-btn${step > 1 ? '' : '-disabled'}`
                  }
                  onClick={
                    step === 1 ? handleClose : () => handleStep('decrease')
                  }
                >
                  <div className='wizard-btn-text-padding'>
                    <div
                      className={
                        step === 1
                          ? 'wizard-back-btn-text'
                          : `wizard-back-btn-text${step > 1 ? '' : '-disabled'}`
                      }
                    >
                      {step === 1 ? 'Close' : 'Back'}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {step < 7 && (
              <div
                className='wizard-primary-btn'
                onClick={() => handleStep('increase')}
              >
                <div className='wizard-next-btn-text'>Next</div>
              </div>
            )}
            {step === 7 && (
              <div
                className='wizard-primary-btn'
                onClick={handleCreateDatabase}
              >
                <div className='wizard-next-btn-text'>
                  {status === 'creating' || status === 'uploading'
                    ? 'Creating...'
                    : 'Create Database'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateDatabaseModal;
